import { Orientation } from 'react-d3-tree/lib/types/types/common';
import {
  MinimalPraksisavtaleDto,
  MinimalPraksisbestillingDto,
  MinimalPraksisbesvarelseDto,
  MinimalPraksisstedDto,
  MinimalPraksisstedtilgangDto,
  MinimalStudentPraksisplassDto,
  PraksisstedMedInfoDto,
  PraksisstedMedInfoOgHierarkiDto,
} from '../../api/generated/admin-generated-api';
import React from 'react';
import { TreeNodeEventCallback } from 'react-d3-tree/lib/types/Tree/types';

export enum NodeCircleTypes {
  MainRelevant = 'MainRelevant',
  MainIrrelevant = 'MainIrrelevant',
  Relevant = 'Relevant',
  Irrelevant = 'Irrelevant',
  Overnode = 'Overnode',
}

export const INIT_LIMIT = 20;
export const INIT_INCLUDE_DELETED = false;

export type Search = {
  searchString: string;
  includeDeleted: boolean;
  limit: number | undefined;
};

export type SearchWithResults = {
  search: Search;
  searchResults: MinimalPraksisstedDto[];
  searchResultsModalOpen: boolean;
  chosenPlace: string | undefined;
};

export type SearchPayload = {
  search?: {
    searchString: string;
    includeDeleted: boolean;
    limit: number | undefined;
  };
  searchResults?: MinimalPraksisstedDto[];
  searchResultsModalOpen?: boolean;
  chosenPlace?: string | undefined;
};

export const initSearchState: SearchWithResults = {
  search: {
    searchString: '',
    includeDeleted: INIT_INCLUDE_DELETED,
    limit: INIT_LIMIT,
  },
  searchResultsModalOpen: false,
  searchResults: [],
  chosenPlace: undefined,
};

export const D3_RENDER_VALUES = {
  ORIENTATION: 'vertical' as Orientation,
  TRANSLATE_X_DIVISOR: 2.5,
  TRANSLATE_Y: 25,
  SEPARATION: { nonSiblings: 2, siblings: 1.75 },
  NODE_SIZE: { x: 100, y: 100 },
  DEPTH_FACTOR: 150,
};

export const MAX_CHARS_NODE_NAME = 20;

export type PraksisstedTreeLoaderProps = {
  chosenPraksisstedId: string;
  includeDeleted: boolean;
};

export type PraksisstedTreeProps = {
  praksissted: PraksisstedMedInfoOgHierarkiDto;
};

export type NodeCircleProps = {
  nodeCircleType: NodeCircleTypes;
  cx?: number;
  cy?: number;
};

export type NodeProps = NodeCircleProps & {
  isLeafNode: boolean;
  name: string;
  onClick: (event: React.SyntheticEvent) => void;
};

export type D3TreeProps = PraksisstedTreeProps & {
  containerWidth: number;
  onNodeClick: TreeNodeEventCallback;
};

export type PraksisstedInfoModalProps = {
  praksissted: PraksisstedMedInfoDto;
  onClose: () => void;
  flattenedUndernoder: PraksisstedMedInfoDto[];
  flattenedTree: PraksisstedMedInfoDto[];
  showOvernodeWarning: boolean;
};

export type AvtalerProps = {
  avtalerOnSelf: MinimalPraksisavtaleDto[];
  avtalerOnChildren: MinimalPraksisavtaleDto[];
};

export type BestillingerProps = {
  bestillingerOnSelf: MinimalPraksisbestillingDto[];
  relevanteBestillinger: MinimalPraksisbestillingDto[];
};

export type BesvarelserProps = {
  besvarelserOnSelf: MinimalPraksisbesvarelseDto[];
  besvarelserOnChildren: MinimalPraksisbesvarelseDto[];
};

export type StudentPraksisplasserProps = {
  studentPraksisplasserOnSelf: MinimalStudentPraksisplassDto[];
  studentPraksisplasserOnChildren: MinimalStudentPraksisplassDto[];
};

export type PraksisstedTilgangerProps = {
  praksisstedtilgangerOnSelf: MinimalPraksisstedtilgangDto[];
  praksisstedtilgangerOnChildren: MinimalPraksisstedtilgangDto[];
};

export type PraksisstedMedInfoDtoArrays = Pick<
  PraksisstedMedInfoDto,
  | 'praksisbestillinger'
  | 'praksisbesvarelser'
  | 'praksisavtaler'
  | 'praksisstedtilganger'
  | 'studentPraksisplasser'
>;

export type SubSectionProps = { header: string };

export type BoldRegularPairProps = {
  boldString: string;
  regularString: string | undefined;
  large?: boolean;
};

export type AvtaleProps = { avtale: MinimalPraksisavtaleDto };

export type BestillingProps = { bestilling: MinimalPraksisbestillingDto };

export type BesvarelseProps = { besvarelse: MinimalPraksisbesvarelseDto };

export type StudentPraksisplassProps = {
  studentPraksisplass: MinimalStudentPraksisplassDto;
};

export type PraksisstedTilgangProps = {
  praksisstedTilgang: MinimalPraksisstedtilgangDto;
};
